/* When this reconciliation runs without anybody asking.
 *
 * **A reconciliation that only runs when somebody presses a button is not a control**: in BFSI it is
 * a daily check, and a check performed when remembered stops being performed the week somebody is on
 * leave. Three things this panel must not collapse:
 *
 * * **Unconfigured is not disabled.** Nobody has considered a schedule, which is a different fact
 *   from somebody having switched one off — and a 404 is deliberately not what the API returns.
 * * **Suspended is not disabled either.** The backend suspends by setting a timestamp so the
 *   schedule resumes by itself; a schedule that has stopped trying but reads as "enabled" is the one
 *   state an operator most needs to see.
 * * **`last_outcome: null` is not an outcome.** It means this cadence has never come due.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { Cadence, RunSchedule } from '@/shared/api/generated/reconciliation';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useRunSchedule, useSaveRunSchedule } from '../api/governance';
import {
  isConfigured,
  outcomeLabelKey,
  outcomeTone,
  scheduleBadge,
  scheduleProblemKey,
  TIMEZONES,
  WEEKDAY_KEYS,
  weekdaysFor,
  type ScheduleDraft,
} from '../model/governance';

const NEW_SCHEDULE: ScheduleDraft = {
  enabled: true,
  cadence: 'daily',
  localTime: '06:00',
  timezone: 'Asia/Kolkata',
  weekdays: [0, 1, 2, 3, 4, 5, 6],
};

function draftFrom(schedule: RunSchedule): ScheduleDraft {
  return {
    enabled: schedule.enabled,
    cadence: schedule.cadence,
    localTime: schedule.local_time,
    timezone: schedule.timezone,
    weekdays: schedule.weekdays,
  };
}

export function RunSchedulePanel({
  definitionId,
  canConfigure,
}: {
  readonly definitionId: string;
  readonly canConfigure: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const reading = useRunSchedule(definitionId);
  const save = useSaveRunSchedule(definitionId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ScheduleDraft>(NEW_SCHEDULE);

  const problemKey = scheduleProblemKey(draft);

  return (
    <section className="lq-card lq-rc__panel">
      <div className="lq-rc__panelHead">
        <h3 className="lq-rc__panelTitle">{t('reconciliation.schedule.title')}</h3>
        {canConfigure ? (
          reading.isSuccess && (
            <button
              type="button"
              className="lq-linkbutton"
              onClick={() => {
                if (open) {
                  setOpen(false);
                  return;
                }
                setDraft(
                  isConfigured(reading.data) ? draftFrom(reading.data) : NEW_SCHEDULE,
                );
                setOpen(true);
              }}
            >
              {t(
                open
                  ? 'reconciliation.schedule.close'
                  : 'reconciliation.schedule.configure',
              )}
            </button>
          )
        ) : (
          <span className="lq-muted" title={t('reconciliation.schedule.gated.why')}>
            {t('reconciliation.schedule.gated')}
          </span>
        )}
      </div>

      {reading.isPending && <SkeletonRows rows={2} columns={1} />}
      {reading.isError && (
        <Alert tone="error" title={t('reconciliation.schedule.failed')}>
          {reading.error.message}
        </Alert>
      )}

      {reading.isSuccess && !isConfigured(reading.data) && (
        <>
          <p className="lq-rc__headline">{t('reconciliation.schedule.none.title')}</p>
          <p className="lq-rc__panelBody">{reading.data.detail}</p>
          <p className="lq-note">{t('reconciliation.schedule.noCatchUp')}</p>
        </>
      )}

      {reading.isSuccess && isConfigured(reading.data) && (
        <ScheduleSummary schedule={reading.data} />
      )}

      {open && (
        <div className="lq-rc__form">
          <label className="lq-rc__check">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(event) => {
                setDraft({ ...draft, enabled: event.target.checked });
              }}
            />
            <strong>{t('reconciliation.schedule.enabled')}</strong>
          </label>

          <div className="lq-rc__grid2">
            <div>
              <div className="lq-rc__formLabel">
                {t('reconciliation.schedule.cadence')}
              </div>
              <select
                className="lq-rc__select"
                value={draft.cadence}
                onChange={(event) => {
                  setDraft({ ...draft, cadence: event.target.value as Cadence });
                }}
              >
                <option value="daily">{t('reconciliation.cadence.daily')}</option>
                <option value="weekly">{t('reconciliation.cadence.weekly')}</option>
              </select>
            </div>
            <div>
              <div className="lq-rc__formLabel">
                {t('reconciliation.schedule.localTime')}
              </div>
              <input
                className="lq-rc__mono"
                value={draft.localTime}
                placeholder="06:00"
                onChange={(event) => {
                  setDraft({ ...draft, localTime: event.target.value });
                }}
              />
            </div>
          </div>

          <div className="lq-rc__formLabel">{t('reconciliation.schedule.timezone')}</div>
          <select
            className="lq-rc__select"
            value={draft.timezone}
            onChange={(event) => {
              setDraft({ ...draft, timezone: event.target.value });
            }}
          >
            {TIMEZONES.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
          <p className="lq-note">{t('reconciliation.schedule.ianaOnly')}</p>

          {draft.cadence === 'weekly' && (
            <>
              <div className="lq-rc__formLabel">
                {t('reconciliation.schedule.weekdays')}
              </div>
              <div className="lq-rc__choices lq-rc__choices--wrap">
                {WEEKDAY_KEYS.map((key, index) => (
                  <label
                    key={key}
                    className={`lq-rc__choice${draft.weekdays.includes(index) ? ' lq-rc__choice--on' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={draft.weekdays.includes(index)}
                      onChange={(event) => {
                        setDraft({
                          ...draft,
                          weekdays: event.target.checked
                            ? [...draft.weekdays, index]
                            : draft.weekdays.filter((day) => day !== index),
                        });
                      }}
                    />
                    {t(key)}
                  </label>
                ))}
              </div>
            </>
          )}

          {problemKey !== null && <p className="lq-rc__error">{t(problemKey)}</p>}
          {save.isError && <p className="lq-rc__error">{save.error.message}</p>}

          <Button
            tone="primary"
            onClick={() => {
              if (problemKey !== null) return;
              save.mutate(
                {
                  enabled: draft.enabled,
                  cadence: draft.cadence,
                  local_time: draft.localTime,
                  timezone: draft.timezone,
                  weekdays: weekdaysFor(draft),
                },
                {
                  onSuccess: () => {
                    setOpen(false);
                  },
                },
              );
            }}
            disabled={problemKey !== null || save.isPending}
          >
            {t('reconciliation.schedule.save')}
          </Button>
        </div>
      )}
    </section>
  );
}

function ScheduleSummary({ schedule }: { readonly schedule: RunSchedule }): ReactElement {
  const { t } = useTranslation();
  const badge = scheduleBadge(schedule);

  return (
    <>
      <span
        className={`lq-rc__badge lq-rc__badge--${badge === 'enabled' ? 'ok' : badge === 'suspended' ? 'warn' : 'muted'}`}
      >
        {t(`reconciliation.schedule.badge.${badge}`)}
      </span>

      {/* Served rather than assembled from four fields, so the cadence reads the same on every
          screen that shows it. */}
      <p className="lq-rc__headline">{schedule.describes}</p>

      {schedule.suspended_until !== null && (
        <div className="lq-rc__suspended">
          <strong>{t('reconciliation.schedule.suspended')}</strong>{' '}
          {schedule.suspension_reason}{' '}
          {t('reconciliation.schedule.resumes', {
            when: new Date(schedule.suspended_until).toLocaleString(),
          })}
        </div>
      )}

      <div className="lq-rc__grid2 lq-rc__facts">
        <div>
          <div className="lq-muted">{t('reconciliation.schedule.nextRun')}</div>
          {/* Null for a disabled schedule *and* while an occurrence is being dispatched — the
              claim nulls it and the outcome recomputes it, so the two can never both be true. */}
          <div className="lq-mono">
            {schedule.next_run_at === null
              ? t('reconciliation.schedule.noNext')
              : new Date(schedule.next_run_at).toLocaleString()}
          </div>
        </div>
        <div>
          <div className="lq-muted">{t('reconciliation.schedule.lastRun')}</div>
          <div className="lq-mono">
            {schedule.last_run_at === null
              ? t('reconciliation.schedule.neverDue')
              : new Date(schedule.last_run_at).toLocaleString()}
          </div>
        </div>
      </div>

      {schedule.last_outcome === null ? (
        <p className="lq-rc__panelBody">{t('reconciliation.schedule.notYetDue')}</p>
      ) : (
        <>
          <p
            className={`lq-rc__outcome lq-rc__outcome--${outcomeTone(schedule.last_outcome)}`}
          >
            <strong>{t(outcomeLabelKey(schedule.last_outcome))}</strong>
          </p>
          <p className="lq-rc__panelBody">{schedule.last_detail}</p>
        </>
      )}
    </>
  );
}
